import {
  calculateNetAmount,
  calculateVatAmount,
  type DraftCostpointAssignment,
  type DraftTransaction,
  type SplitTransactionRequest,
} from '@finapp/types';
import type React from 'react';
import { useEffect, useState } from 'react';
import { CostpointAssignmentEditor } from './CostpointAssignmentEditor';
import SplitModal from './SplitModal';

interface DraftTransactionItemProps {
  transaction: DraftTransaction;
  isSelected: boolean;
  isMultiSelected?: boolean;
  onSelect: () => void;
  onMultiSelect?: () => void;
  onUpdate: (updates: Partial<DraftTransaction>) => void;
  onSplitTransaction?: (transactionId: string, splits: SplitTransactionRequest) => Promise<void>;
}

const DraftTransactionItemComponent: React.FC<DraftTransactionItemProps> = ({
  transaction,
  isSelected,
  isMultiSelected = false,
  onSelect,
  onMultiSelect,
  onUpdate,
  onSplitTransaction,
}) => {
  const [isEditingHeadingExpanded, setIsEditingHeadingExpanded] = useState(false);
  const [editedHeading, setEditedHeading] = useState(transaction.heading || '');
  const [isEditingDescriptionExpanded, setIsEditingDescriptionExpanded] = useState(false);
  const [editedDescription, setEditedDescription] = useState(transaction.description || '');
  const [isEditingVAT, setIsEditingVAT] = useState(false);
  const [showSplitModal, setShowSplitModal] = useState(false);

  const [editedHasVat, setEditedHasVat] = useState(transaction.has_vat || false);
  const [editedVatRate, setEditedVatRate] = useState<number>(transaction.vat_rate?.rate || 0);

  // Sync state with props when transaction changes
  useEffect(() => {
    setEditedHeading(transaction.heading || '');
    setEditedDescription(transaction.description || '');
    setEditedHasVat(transaction.has_vat || false);
    setEditedVatRate(transaction.vat_rate?.rate || 0);
  }, [transaction.heading, transaction.description, transaction.has_vat, transaction.vat_rate]);

  const formatAmount = (amount: number) => {
    return new Intl.NumberFormat('fi-FI', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fi-FI');
  };

  const getTotalAssignedAmount = () => {
    return transaction.costpoints.reduce((sum, cp) => sum + cp.amount, 0);
  };

  const getTotalAssignedPercentage = () => {
    return transaction.costpoints.reduce((sum, cp) => sum + cp.percentage, 0);
  };

  const isComplete = transaction.is_complete;
  const hasErrors = transaction.validation_errors.length > 0;
  const totalAssigned = getTotalAssignedAmount();
  const totalPercentage = getTotalAssignedPercentage();
  const amountMismatch = Math.abs(totalAssigned - Math.abs(transaction.amount)) > 0.01;

  const handleCostpointsUpdate = (costpoints: DraftCostpointAssignment[]) => {
    const isComplete =
      costpoints.length > 0 &&
      Math.abs(costpoints.reduce((sum, cp) => sum + cp.percentage, 0) - 100) < 0.01;

    onUpdate({
      costpoints,
      is_complete: isComplete,
      // Clear validation errors when costpoints are assigned
      validation_errors: isComplete ? [] : transaction.validation_errors,
    });
  };

  const handleSplit = async (splits: SplitTransactionRequest) => {
    if (onSplitTransaction) {
      try {
        await onSplitTransaction(transaction.id, splits);
        setShowSplitModal(false);
      } catch (error) {
        console.error('Failed to split transaction:', error);
        // You might want to show an error message to the user here
      }
    }
  };

  const handleHeadingSave = () => {
    const trimmedHeading = editedHeading?.trim() || '';
    console.log('handleHeadingSave called:', {
      trimmedHeading,
      currentHeading: transaction.heading,
      isDifferent: trimmedHeading !== (transaction.heading || ''),
    });

    if (trimmedHeading !== (transaction.heading || '')) {
      console.log('Saving heading:', {
        from: transaction.heading,
        to: trimmedHeading,
      });
      onUpdate({ heading: trimmedHeading });
    } else {
      console.log('No changes to save for heading');
    }
    setIsEditingHeadingExpanded(false);
  };

  const handleHeadingCancel = () => {
    setEditedHeading(transaction.heading || '');
    setIsEditingHeadingExpanded(false);
  };

  const handleDescriptionSave = () => {
    const trimmedDescription = editedDescription?.trim() || '';
    console.log('handleDescriptionSave called:', {
      trimmedDescription,
      currentDescription: transaction.description,
      isDifferent: trimmedDescription !== (transaction.description || ''),
    });

    if (trimmedDescription !== (transaction.description || '')) {
      console.log('Saving description:', {
        from: transaction.description,
        to: trimmedDescription,
      });
      onUpdate({ description: trimmedDescription });
    } else {
      console.log('No changes to save for description');
    }
    setIsEditingDescriptionExpanded(false);
  };

  const handleDescriptionCancel = () => {
    setEditedDescription(transaction.description || '');
    setIsEditingDescriptionExpanded(false);
  };

  const handleHeadingKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleHeadingSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleHeadingCancel();
    }
  };

  const handleDescriptionKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handleDescriptionSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleDescriptionCancel();
    }
  };

  const handleVATSave = () => {
    const netAmount = calculateNetAmount(transaction.amount, editedHasVat, editedVatRate);
    const vatAmount = calculateVatAmount(transaction.amount, editedHasVat, editedVatRate);

    // Map VAT rate to vat_id based on the database values
    const getVatIdFromRate = (rate: number): number | null => {
      if (!editedHasVat) return null;
      switch (rate) {
        case 25.5:
          return 2; // General VAT
        case 14.0:
          return 3; // Reduced - Food (assuming ID 3)
        case 10.0:
          return 4; // Reduced - Books (assuming ID 4)
        case 0.0:
          return 1; // Zero VAT (assuming ID 1)
        default:
          return null;
      }
    };

    onUpdate({
      has_vat: editedHasVat,
      vat_id: getVatIdFromRate(editedVatRate),
      net_amount: netAmount,
      vat_amount: vatAmount,
    });
    setIsEditingVAT(false);
  };

  const handleVATCancel = () => {
    setEditedHasVat(transaction.has_vat || false);
    setEditedVatRate(
      typeof transaction.vat_rate === 'object'
        ? transaction.vat_rate.rate
        : transaction.vat_rate || 0
    );
    setIsEditingVAT(false);
  };

  const handleVATRemove = () => {
    console.log('Removing VAT from transaction');

    // Calculate net amount when VAT is removed (net = gross)
    const netAmount = transaction.amount;
    const vatAmount = 0;

    onUpdate({
      has_vat: false,
      vat_id: null,
      net_amount: netAmount,
      vat_amount: vatAmount,
    });
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border transition-all duration-200 ${
        isSelected
          ? 'ring-2 ring-blue-500 border-blue-300'
          : hasErrors
            ? 'border-red-300'
            : isComplete
              ? 'border-green-300'
              : 'border-gray-200'
      }`}
      data-testid="transaction-row"
    >
      {/* Transaction Header */}
      <div className="p-4 cursor-pointer hover:bg-gray-50" onClick={onSelect}>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Multi-select Checkbox */}
            {onMultiSelect && (
              <input
                type="checkbox"
                checked={isMultiSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  onMultiSelect();
                }}
                onClick={(e) => e.stopPropagation()}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
            )}

            {/* Status Indicator */}
            <div
              className={`w-3 h-3 rounded-full ${
                hasErrors ? 'bg-red-500' : isComplete ? 'bg-green-500' : 'bg-yellow-500'
              }`}
            />

            {/* Transaction Info */}
            <div>
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-500">{formatDate(transaction.date)}</span>
                <div className="flex flex-col">
                  <span
                    className={`font-medium ${
                      transaction.type === 'deposit' ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    {formatAmount(transaction.amount)}
                    {transaction.has_vat && (
                      <span className="text-xs text-gray-500 ml-1">(gross)</span>
                    )}
                  </span>
                  {transaction.has_vat && (
                    <span className="text-xs text-gray-600">
                      Net: {formatAmount(transaction.net_amount || 0)}
                    </span>
                  )}
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded ${
                    transaction.type === 'deposit'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                  }`}
                >
                  {transaction.type.toUpperCase()}
                </span>
              </div>

              {/* Transaction Heading - Display Only */}
              <div className="mt-1">
                <p className="text-gray-900 font-medium">
                  {transaction.heading || transaction.original_heading || 'No heading'}
                </p>
              </div>
            </div>
          </div>

          {/* Quick Status */}
          <div className="flex items-center space-x-2">
            {transaction.costpoints.length > 0 && (
              <div className="text-xs text-gray-500">
                {transaction.costpoints.length} costpoint
                {transaction.costpoints.length !== 1 ? 's' : ''}
              </div>
            )}

            {/* Tags Display */}
            {(() => {
              // Get all tags from both transaction level and costpoints
              const transactionTags = transaction.tags || [];
              const costpointTags = transaction.costpoints.flatMap((cp) => cp.tags || []);
              const allTags = [...transactionTags, ...costpointTags];

              // Remove duplicates based on tag id
              const uniqueTags = allTags.filter(
                (tag, index, self) => self.findIndex((t) => t.id === tag.id) === index
              );

              return uniqueTags.length > 0 ? (
                <div className="flex flex-wrap gap-1" data-testid="transaction-tags">
                  {uniqueTags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 text-xs bg-green-100 text-green-800 rounded-full"
                      title={tag.display_name}
                      data-testid={`tag-${tag.name}`}
                    >
                      {tag.display_name}
                    </span>
                  ))}
                </div>
              ) : null;
            })()}

            <svg
              className={`w-5 h-5 transition-transform duration-200 ${
                isSelected ? 'transform rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {/* Error Messages */}
        {hasErrors && (
          <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded">
            <div className="text-sm text-red-800">
              {transaction.validation_errors.map((error, index) => (
                <div key={index} className="flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {error}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Expanded Content */}
      {isSelected && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="space-y-4">
            {/* Heading Field */}
            <div className="bg-white p-3 rounded border">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">Transaction Heading</h4>
                {!isEditingHeadingExpanded && (
                  <button
                    onClick={() => setIsEditingHeadingExpanded(true)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {transaction.heading ? 'Edit' : 'Add heading'}
                  </button>
                )}
              </div>

              {isEditingHeadingExpanded ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editedHeading}
                    onChange={(e) => setEditedHeading(e.target.value)}
                    onKeyDown={handleHeadingKeyDown}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Enter a descriptive heading for this transaction..."
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={handleHeadingSave}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      data-testid="save-heading-button"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleHeadingCancel}
                      className="px-3 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-700">
                  {transaction.heading || (
                    <span className="text-gray-400 italic">
                      No heading set. Original: {transaction.original_heading || 'N/A'}
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Description Field */}
            <div className="bg-white p-3 rounded border">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">Description</h4>
                {!isEditingDescriptionExpanded && (
                  <button
                    onClick={() => setIsEditingDescriptionExpanded(true)}
                    className="text-sm text-blue-600 hover:text-blue-800"
                    data-testid="add-description-button"
                  >
                    {transaction.description ? 'Edit' : 'Add description'}
                  </button>
                )}
              </div>

              {isEditingDescriptionExpanded ? (
                <div className="space-y-2">
                  <textarea
                    value={editedDescription}
                    onChange={(e) => setEditedDescription(e.target.value)}
                    onKeyDown={handleDescriptionKeyDown}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                    placeholder="Add notes or additional information about this transaction..."
                    rows={3}
                    data-testid="description-textarea"
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={handleDescriptionSave}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      data-testid="save-description-button"
                    >
                      Save
                    </button>
                    <button
                      onClick={handleDescriptionCancel}
                      className="px-3 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-700">
                  {transaction.description ? (
                    <p className="whitespace-pre-wrap">{transaction.description}</p>
                  ) : (
                    <p className="text-gray-500 italic">No description added yet</p>
                  )}
                </div>
              )}
            </div>

            {/* VAT Settings */}
            <div className="bg-white p-3 rounded border">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900">VAT Settings</h4>
                {!isEditingVAT && (
                  <div className="flex space-x-3">
                    {transaction.has_vat && (
                      <button
                        onClick={handleVATRemove}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove VAT
                      </button>
                    )}
                    <button
                      onClick={() => setIsEditingVAT(true)}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      Edit VAT
                    </button>
                  </div>
                )}
              </div>

              {isEditingVAT ? (
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editedHasVat}
                        onChange={(e) => {
                          setEditedHasVat(e.target.checked);
                          if (!e.target.checked) {
                            setEditedVatRate(0);
                          } else if (editedVatRate === 0) {
                            setEditedVatRate(25.5); // Current Finnish standard VAT
                          }
                        }}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        This transaction includes VAT
                      </span>
                    </label>
                  </div>

                  {editedHasVat && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        VAT Rate
                      </label>
                      <select
                        value={editedVatRate}
                        onChange={(e) => setEditedVatRate(Number(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm"
                        data-testid="vat-rate-select"
                      >
                        <option value={25.5}>25.5% (General VAT)</option>
                        <option value={14.0}>14.0% (Reduced - Food)</option>
                        <option value={10.0}>10.0% (Reduced - Books)</option>
                        <option value={0.0}>0.0% (Zero VAT)</option>
                      </select>
                    </div>
                  )}

                  {editedHasVat && (
                    <div className="bg-gray-50 p-2 rounded text-sm">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <span className="text-gray-600">Gross Amount:</span>
                          <span className="ml-2 font-medium">
                            {formatAmount(transaction.amount)}
                          </span>
                        </div>
                        <div>
                          <span className="text-gray-600">VAT Amount:</span>
                          <span className="ml-2 font-medium">
                            {formatAmount(
                              calculateVatAmount(transaction.amount, editedHasVat, editedVatRate)
                            )}
                          </span>
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-600">Net Amount (for budgets):</span>
                          <span className="ml-2 font-medium text-blue-600">
                            {formatAmount(
                              calculateNetAmount(transaction.amount, editedHasVat, editedVatRate)
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="flex space-x-2">
                    <button
                      onClick={handleVATSave}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                      data-testid="save-vat-settings-button"
                    >
                      Save VAT Settings
                    </button>
                    <button
                      onClick={handleVATCancel}
                      className="px-3 py-1 text-xs bg-gray-300 text-gray-700 rounded hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-gray-700">
                  {transaction.has_vat ? (
                    <div className="space-y-1">
                      <div className="flex justify-between">
                        <span>VAT Rate:</span>
                        <span className="font-medium">
                          {typeof transaction.vat_rate === 'object'
                            ? transaction.vat_rate.rate
                            : transaction.vat_rate}
                          %
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Gross Amount:</span>
                        <span className="font-medium">{formatAmount(transaction.amount)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>VAT Amount:</span>
                        <span className="font-medium">
                          {formatAmount(transaction.vat_amount || 0)}
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-1">
                        <span className="text-blue-600">Net Amount (for budgets):</span>
                        <span className="font-medium text-blue-600">
                          {formatAmount(transaction.net_amount || 0)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-gray-500 italic">
                      No VAT applied - full amount used for budget comparisons
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Assignment Summary */}
            {transaction.costpoints.length > 0 && (
              <div className="bg-white p-3 rounded border">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Current Assignment</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Assigned Amount:</span>
                    <span
                      className={`ml-2 font-medium ${
                        amountMismatch ? 'text-red-600' : 'text-gray-900'
                      }`}
                    >
                      {formatAmount(totalAssigned)}
                    </span>
                  </div>
                  <div>
                    <span className="text-gray-500">Total Percentage:</span>
                    <span
                      className={`ml-2 font-medium ${
                        Math.abs(totalPercentage - 100) > 0.01 ? 'text-red-600' : 'text-gray-900'
                      }`}
                    >
                      {totalPercentage.toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Costpoint Assignment Editor */}
            <CostpointAssignmentEditor
              transaction={transaction}
              onUpdate={handleCostpointsUpdate}
            />

            {/* Split Transaction Button */}
            {onSplitTransaction && (
              <div className="bg-white p-3 rounded border">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Transaction Actions</h4>
                <button
                  onClick={() => setShowSplitModal(true)}
                  className="px-4 py-2 text-sm bg-purple-100 text-purple-800 rounded hover:bg-purple-200 transition-colors"
                >
                  Split Transaction
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Split Modal */}
      {showSplitModal && (
        <SplitModal
          transaction={transaction}
          onSplit={handleSplit}
          onClose={() => setShowSplitModal(false)}
        />
      )}
    </div>
  );
};

// Export the component directly to avoid React.memo issues with state updates
export const DraftTransactionItem = DraftTransactionItemComponent;
